import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import { createSelector } from 'reselect';
import { TICKET_TYPE_STANDALONE } from 'common/entities/ticketType/selectors';
import {
    BRAND_LIBRARY_DARK_TEXT_COLOR,
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BODY_TEXT,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_TEXT,
} from 'common/constants/brandLibrary';
import { getEntitySelector } from '../../selectors';
import { getForm } from '../selectors';
import { ENTITIES_KEY_COLOR } from '../../color';
import { ENTITIES_KEY_FONT } from '../../font';
import {
    DEFAULT_FONT,
    DEFAULT_FORM_SESSION_STEPPER_FONT_SIZE,
    DEFAULT_SMALL_TICKET_FONT_SIZE,
    DEFAULT_BUTTON_BACKGROUND_COLOR,
    DEFAULT_BUTTON_LABEL_COLOR,
    DEFAULT_FORM_SESSION_BODY_SIZE,
    DEFAULT_FORM_SESSION_TITLE_SIZE,
    DEFAULT_FORM_SESSION_ELEMENT_BORDER_COLOR,
    DEFAULT_FORM_SESSION_TAG_BORDER_RADIUS,
    DEFAULT_FORM_SESSION_ELEMENT_BORDER_WIDTH,
    DEFAULT_FORM_SESSION_BADGE_BORDER_RADIUS,
    DEFAULT_FORM_SESSION_BADGE_BACKGROUND_COLOR,
    DEFAULT_FORM_SESSION_BADGE_COLOR,
    DEFAULT_FORM_SESSION_TAG_COLOR,
    DEFAULT_FORM_SESSION_TAG_BACKGROUND_COLOR,
    DEFAULT_FORM_SESSION_FONT_COLOR,
    DEFAULT_BORDER_WIDTH,
    DEFAULT_BUTTON_BORDER_RADIUS,
    DEFAULT_FONT_SIZE,
    DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND,
    DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR,
    DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR,
    DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR_HOVER,
} from './defaultValues';
import { getDefaultStyle } from './utils';

export const getFormStyleConfiguration = (state, formId) => {
    const form = getForm(state, formId);

    return form && form.styleConfiguration;
};

export const getFormStyleProperty = (state, formId, stylePath) => {
    const formStyleConfiguration = getFormStyleConfiguration(state, formId);

    return get(formStyleConfiguration, stylePath);
};

export const getFormEntityStyle = (state, formId, path, entityName) => {
    const styleId = getFormStyleProperty(state, formId, path);
    const styleEntity = getEntitySelector(state, entityName);

    return !isUndefined(styleId) && styleEntity && styleEntity[styleId] ? styleEntity[styleId] : undefined;
};

export const getFormColorGroup = (state, formId, groupPath) => ({
    default: getFormEntityStyle(state, formId, `${groupPath}.default`, ENTITIES_KEY_COLOR),
    hover: getFormEntityStyle(state, formId, `${groupPath}.hover`, ENTITIES_KEY_COLOR),
});

export const getFontStyles = (state, formId, groupPath) => ({
    font: getFormEntityStyle(state, formId, `${groupPath}.font`, ENTITIES_KEY_FONT),
    fontSize: getFormStyleProperty(state, formId, `${groupPath}.fontSize`),
    color: getFormEntityStyle(state, formId, `${groupPath}.color`, ENTITIES_KEY_COLOR),
});

export const getBorderStyles = (state, formId, groupPath) => ({
    radius: getFormStyleProperty(state, formId, `${groupPath}.radius`),
    sizes: {
        top: getFormStyleProperty(state, formId, `${groupPath}.sizes.top`),
        bottom: getFormStyleProperty(state, formId, `${groupPath}.sizes.bottom`),
        right: getFormStyleProperty(state, formId, `${groupPath}.sizes.right`),
        left: getFormStyleProperty(state, formId, `${groupPath}.sizes.left`),
    },
    colors: {
        default: getFormEntityStyle(state, formId, `${groupPath}.colors.default`, ENTITIES_KEY_COLOR),
        hover: getFormEntityStyle(state, formId, `${groupPath}.colors.hover`, ENTITIES_KEY_COLOR),
        focus: getFormEntityStyle(state, formId, `${groupPath}.colors.focus`, ENTITIES_KEY_COLOR),
    },
});

export const getFormColors = (state, formId) => ({
    background: getFormEntityStyle(state, formId, 'form.colors.background', ENTITIES_KEY_COLOR),
    text: getFormEntityStyle(state, formId, 'form.colors.text', ENTITIES_KEY_COLOR),
    input: getFormEntityStyle(state, formId, 'form.colors.input', ENTITIES_KEY_COLOR),
    accent: getFormEntityStyle(state, formId, 'form.colors.accent', ENTITIES_KEY_COLOR),
});

export const getFormFonts = (state, formId) => ({
    primary: getFormEntityStyle(state, formId, 'form.fonts.primary', ENTITIES_KEY_FONT),
    secondary: getFormEntityStyle(state, formId, 'form.fonts.secondary', ENTITIES_KEY_FONT),
});

export const getFormBackgroundImage = (state, formId) => ({
    url: getFormStyleProperty(state, formId, 'form.backgroundImage.url'),
    baseUrl: getFormStyleProperty(state, formId, 'form.backgroundImage.baseUrl'),
});

export const getFormDisplayStyles = (state, formId) => ({
    displayMode: getFormStyleProperty(state, formId, 'form.displayStyles.displayMode'),
    panelDisplay: getFormStyleProperty(state, formId, 'form.displayStyles.panelDisplay'),
});

export const getFormQuestionTextStyles = (state, formId) => getFontStyles(state, formId, 'formFields.questionText');

export const getFormQuestionLabelColor = (state, formId) => {
    const questionTextStyles = getFormQuestionTextStyles(state, formId);
    return get(questionTextStyles, 'color.value');
};

export const getFormInputTextStyles = (state, formId) => ({
    font: getFormEntityStyle(state, formId, 'formFields.inputText.font', ENTITIES_KEY_FONT),
    fontSize: getFormStyleProperty(state, formId, 'formFields.inputText.fontSize'),
    colors: {
        answer: getFormEntityStyle(state, formId, 'formFields.inputText.colors.answer', ENTITIES_KEY_COLOR),
        placeholder: getFormEntityStyle(state, formId, 'formFields.inputText.colors.placeholder', ENTITIES_KEY_COLOR),
    },
});

export const getFormInputHelperTextStyles = (state, formId) => getFontStyles(state, formId, 'formFields.helperText');

export const getFormBorderStyles = (state, formId) => getBorderStyles(state, formId, 'formFields.border');

export const getFormInputBackgroundColors = (state, formId) =>
    getFormColorGroup(state, formId, 'formFields.inputBackgroundColors');

export const getFormButtonColors = (state, formId) => getFormColorGroup(state, formId, 'button.colors');

export const getFormButtonTextStyles = (state, formId) => ({
    font: getFormEntityStyle(state, formId, 'button.text.font', ENTITIES_KEY_FONT),
    fontSize: getFormStyleProperty(state, formId, 'button.text.fontSize'),
    colors: getFormColorGroup(state, formId, 'button.text.colors'),
});

export const getFormButtonBorderStyles = (state, formId) => getBorderStyles(state, formId, 'button.border');

export const getButtonPosition = (state, formId) => getFormStyleProperty(state, formId, 'button.position');

export const getHeadLineTextStyles = (state, formId) => getFontStyles(state, formId, 'headLineText');

export const getHeadLineTextPosition = (state, formId) => getFormStyleProperty(state, formId, 'headLineText.position');

export const getFormErrorStyles = (state, formId) => getFontStyles(state, formId, 'errors');

export const getFormHeaderImageStyles = (state, formId) => ({
    size: getFormStyleProperty(state, formId, 'headerImage.size'),
    type: getFormStyleProperty(state, formId, 'headerImage.type'),
    url: getFormStyleProperty(state, formId, 'headerImage.url'),
    baseUrl: getFormStyleProperty(state, formId, 'headerImage.baseUrl'),
    position: getFormStyleProperty(state, formId, 'headerImage.position'),
    altText: getFormStyleProperty(state, formId, 'headerImage.altText'),
});

export const getFormContentStyles = (state, formId) => ({
    ...getFontStyles(state, formId, 'formContent'),
    position: getFormStyleProperty(state, formId, 'formContent.position'),
});

export const getFormCloseButtonStyles = (state, formId) => ({
    default: getFormEntityStyle(state, formId, 'closeButton.default', ENTITIES_KEY_COLOR),
    hover: getFormEntityStyle(state, formId, 'closeButton.hover', ENTITIES_KEY_COLOR),
    focus: getFormEntityStyle(state, formId, 'closeButton.focus', ENTITIES_KEY_COLOR),
});

export const getFormLinksStyles = (state, formId) => ({
    default: getFormEntityStyle(state, formId, 'form.links.default', ENTITIES_KEY_COLOR),
    hover: getFormEntityStyle(state, formId, 'form.links.hover', ENTITIES_KEY_COLOR),
    focus: getFormEntityStyle(state, formId, 'form.links.focus', ENTITIES_KEY_COLOR),
});

export const getStepperFontStyles = (state, formId) => {
    const fontEntities = getEntitySelector(state, ENTITIES_KEY_FONT);
    const stepperFont = getFormEntityStyle(state, formId, 'stepper.font', ENTITIES_KEY_FONT);
    const stepperFontSize = getFormStyleProperty(state, formId, 'stepper.fontSize');

    return {
        font: stepperFont ?? fontEntities?.[BRAND_LIBRARY_BODY_TEXT] ?? getDefaultStyle(DEFAULT_FONT),
        fontSize: stepperFontSize ?? DEFAULT_FORM_SESSION_STEPPER_FONT_SIZE,
    };
};

export const getStepperPrimaryColor = (state, formId) => {
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);
    const stepperPrimaryColor = getFormEntityStyle(state, formId, 'stepper.primaryColor', ENTITIES_KEY_COLOR);
    return stepperPrimaryColor ?? colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ?? DEFAULT_BUTTON_BACKGROUND_COLOR;
};

export const getStepperSecondaryColor = (state, formId) => {
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);
    const stepperSecondaryColor = getFormEntityStyle(state, formId, 'stepper.secondaryColor', ENTITIES_KEY_COLOR);
    return stepperSecondaryColor ?? colorEntities?.[BRAND_LIBRARY_LIGHT_TEXT_COLOR] ?? DEFAULT_BUTTON_LABEL_COLOR;
};

export const getFormSessionDetailsTitleStyles = (state, formId) => ({
    font:
        getFormEntityStyle(state, formId, 'sessionDetails.title.font', ENTITIES_KEY_FONT) ??
        getFormEntityStyle(state, formId, 'headLineText.font', ENTITIES_KEY_FONT) ??
        getDefaultStyle(DEFAULT_FONT),
    fontSize: getFormStyleProperty(state, formId, 'sessionDetails.title.fontSize') ?? DEFAULT_FORM_SESSION_TITLE_SIZE,
    color:
        getFormEntityStyle(state, formId, 'sessionDetails.title.color', ENTITIES_KEY_COLOR) ??
        getFormEntityStyle(state, formId, 'formFields.inputText.colors.answer', ENTITIES_KEY_COLOR) ??
        getDefaultStyle(DEFAULT_FORM_SESSION_FONT_COLOR),
});

export const getFormSessionDetailsInfoStyles = (state, formId) => {
    const fontEntities = getEntitySelector(state, ENTITIES_KEY_FONT);
    return {
        font:
            getFormEntityStyle(state, formId, 'sessionDetails.info.font', ENTITIES_KEY_FONT) ??
            fontEntities[BRAND_LIBRARY_BODY_TEXT] ??
            getDefaultStyle(DEFAULT_FONT),
        fontSize: getFormStyleProperty(state, formId, 'sessionDetails.info.fontSize') ?? DEFAULT_FORM_SESSION_BODY_SIZE,
        color:
            getFormEntityStyle(state, formId, 'sessionDetails.info.color', ENTITIES_KEY_COLOR) ??
            getFormEntityStyle(state, formId, 'formFields.inputText.colors.answer', ENTITIES_KEY_COLOR) ??
            getDefaultStyle(DEFAULT_FORM_SESSION_FONT_COLOR),
    };
};

export const getFormSessionDetailsTagStyles = (state, formId) => {
    const fontEntities = getEntitySelector(state, ENTITIES_KEY_FONT);
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);

    return {
        font:
            getFormEntityStyle(state, formId, 'sessionDetails.tag.font', ENTITIES_KEY_FONT) ??
            fontEntities[BRAND_LIBRARY_BODY_TEXT] ??
            getDefaultStyle(DEFAULT_FONT),
        fontSize: getFormStyleProperty(state, formId, 'sessionDetails.tag.fontSize') ?? DEFAULT_FORM_SESSION_BODY_SIZE,
        color:
            getFormEntityStyle(state, formId, 'sessionDetails.tag.color', ENTITIES_KEY_COLOR) ??
            getFormEntityStyle(state, formId, 'formFields.inputText.colors.answer', ENTITIES_KEY_COLOR) ??
            getDefaultStyle(DEFAULT_FORM_SESSION_TAG_COLOR),
        background:
            getFormEntityStyle(state, formId, 'sessionDetails.tag.background', ENTITIES_KEY_COLOR) ??
            colorEntities[BRAND_LIBRARY_BUTTON_DEFAULT_COLOR] ??
            getDefaultStyle(DEFAULT_FORM_SESSION_TAG_BACKGROUND_COLOR),
    };
};

export const getFormSessionDetailsBadgeStyles = (state, formId) => {
    const fontEntities = getEntitySelector(state, ENTITIES_KEY_FONT);
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);

    return {
        font:
            getFormEntityStyle(state, formId, 'sessionDetails.badge.font', ENTITIES_KEY_FONT) ??
            fontEntities[BRAND_LIBRARY_BODY_TEXT] ??
            getDefaultStyle(DEFAULT_FONT),
        fontSize:
            getFormStyleProperty(state, formId, 'sessionDetails.badge.fontSize') ?? DEFAULT_FORM_SESSION_BODY_SIZE,
        color:
            getFormEntityStyle(state, formId, 'sessionDetails.badge.color', ENTITIES_KEY_COLOR) ??
            getFormEntityStyle(state, formId, 'formFields.inputText.colors.answer', ENTITIES_KEY_COLOR) ??
            getDefaultStyle(DEFAULT_FORM_SESSION_BADGE_COLOR),
        background:
            getFormEntityStyle(state, formId, 'sessionDetails.badge.background', ENTITIES_KEY_COLOR) ??
            colorEntities[BRAND_LIBRARY_BUTTON_DEFAULT_COLOR] ??
            getDefaultStyle(DEFAULT_FORM_SESSION_BADGE_BACKGROUND_COLOR),
    };
};

const getFormSessionBorderStyles = (state, formId, path) => {
    const defaultBorderRadis =
        path === 'sessionDetails.tag'
            ? DEFAULT_FORM_SESSION_TAG_BORDER_RADIUS
            : DEFAULT_FORM_SESSION_BADGE_BORDER_RADIUS;

    return {
        radius: getFormStyleProperty(state, formId, `${path}.border.radius`) ?? defaultBorderRadis,
        sizes: {
            top:
                getFormStyleProperty(state, formId, `${path}.border.sizes.top`) ??
                DEFAULT_FORM_SESSION_ELEMENT_BORDER_WIDTH,
            bottom:
                getFormStyleProperty(state, formId, `${path}.border.sizes.bottom`) ??
                DEFAULT_FORM_SESSION_ELEMENT_BORDER_WIDTH,
            right:
                getFormStyleProperty(state, formId, `${path}.border.sizes.right`) ??
                DEFAULT_FORM_SESSION_ELEMENT_BORDER_WIDTH,
            left:
                getFormStyleProperty(state, formId, `${path}.border.sizes.left`) ??
                DEFAULT_FORM_SESSION_ELEMENT_BORDER_WIDTH,
        },
        color:
            getFormEntityStyle(state, formId, `${path}.border.color`, ENTITIES_KEY_COLOR) ??
            getDefaultStyle(DEFAULT_FORM_SESSION_ELEMENT_BORDER_COLOR),
    };
};

export const getTagBorderStyles = (state, formId) => getFormSessionBorderStyles(state, formId, 'sessionDetails.tag');

export const getBadgeBorderStyles = (state, formId) =>
    getFormSessionBorderStyles(state, formId, 'sessionDetails.badge');

export const getFormSecondaryButtonColors = (state, formId) => {
    const defaultColor = getFormEntityStyle(state, formId, 'secondaryButton.colors.default', ENTITIES_KEY_COLOR);
    const hoverColor = getFormEntityStyle(state, formId, 'secondaryButton.colors.hover', ENTITIES_KEY_COLOR);
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);

    return {
        default:
            defaultColor ??
            colorEntities?.[BRAND_LIBRARY_LIGHT_TEXT_COLOR] ??
            getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND),
        hover:
            hoverColor ??
            colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ??
            getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_BACKGROUND),
    };
};

export const getFormSecondaryButtonTextStyles = (state, formId) => {
    const fontEntities = getEntitySelector(state, ENTITIES_KEY_FONT);
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);
    const font = getFormEntityStyle(state, formId, 'secondaryButton.text.font', ENTITIES_KEY_FONT);
    const fontSize = getFormStyleProperty(state, formId, 'secondaryButton.text.fontSize');
    const defaultColor = getFormEntityStyle(state, formId, 'secondaryButton.text.colors.default', ENTITIES_KEY_COLOR);
    const hoverColor = getFormEntityStyle(state, formId, 'secondaryButton.text.colors.hover', ENTITIES_KEY_COLOR);

    return {
        font: font ?? fontEntities[BRAND_LIBRARY_BUTTON_TEXT] ?? getDefaultStyle(DEFAULT_FONT),
        fontSize: fontSize ?? DEFAULT_FONT_SIZE,
        colors: {
            default:
                defaultColor ??
                colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ??
                getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR),
            hover:
                hoverColor ??
                colorEntities?.[BRAND_LIBRARY_LIGHT_TEXT_COLOR] ??
                getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_LABEL_COLOR_HOVER),
        },
    };
};

export const getFormSecondaryButtonBorderStyles = (state, formId) => {
    const colorEntities = getEntitySelector(state, ENTITIES_KEY_COLOR);

    return {
        radius: getFormStyleProperty(state, formId, 'secondaryButton.border.radius') ?? DEFAULT_BUTTON_BORDER_RADIUS,
        sizes: {
            top: getFormStyleProperty(state, formId, 'secondaryButton.border.sizes.top') ?? DEFAULT_BORDER_WIDTH,
            bottom: getFormStyleProperty(state, formId, 'secondaryButton.border.sizes.bottom') ?? DEFAULT_BORDER_WIDTH,
            right: getFormStyleProperty(state, formId, 'secondaryButton.border.sizes.right') ?? DEFAULT_BORDER_WIDTH,
            left: getFormStyleProperty(state, formId, 'secondaryButton.border.sizes.left') ?? DEFAULT_BORDER_WIDTH,
        },
        colors: {
            default:
                getFormEntityStyle(state, formId, 'secondaryButton.border.colors.default', ENTITIES_KEY_COLOR) ??
                colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ??
                getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR),
            hover:
                getFormEntityStyle(state, formId, 'secondaryButton.border.colors.hover', ENTITIES_KEY_COLOR) ??
                colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ??
                getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR),
            focus:
                getFormEntityStyle(state, formId, 'secondaryButton.border.colors.focus', ENTITIES_KEY_COLOR) ??
                colorEntities?.[BRAND_LIBRARY_DARK_TEXT_COLOR] ??
                getDefaultStyle(DEFAULT_FORM_SECONDARY_BUTTON_BORDER_COLOR),
        },
    };
};

// //////////////////////////////////////////////////////////////////////////////////////////
// Ticket styles
export const getTicketStyles = (state, formId) => {
    const styleConfiguration = getFormStyleConfiguration(state, formId);
    return (styleConfiguration && styleConfiguration.tickets) || [];
};

export const getTicketStyleCategories = createSelector(getTicketStyles, (ticketStyles) =>
    ticketStyles.map((ticketStyle) => ticketStyle.type).filter(Boolean),
);

export const getTicketStylesByCategory = (state, formId, category) => {
    const ticketStyles = getTicketStyles(state, formId);
    return (
        ticketStyles.find((ticketStyle) => ticketStyle.type === category) ||
        ticketStyles.find((ticketStyle) => ticketStyle.type === TICKET_TYPE_STANDALONE) ||
        {}
    );
};

export const getTicketFormEntityStyle = (state, formId, styleId, entityName) => {
    const styleEntity = getEntitySelector(state, entityName);

    return !isUndefined(styleId) && styleEntity && styleEntity[styleId] ? styleEntity[styleId] : undefined;
};

export const getTicketFontStyles = (state, formId, { font, fontSize, color }) => ({
    fontSize,
    font: getTicketFormEntityStyle(state, formId, font, ENTITIES_KEY_FONT),
    color: getTicketFormEntityStyle(state, formId, color, ENTITIES_KEY_COLOR),
});

export const geTicketBorders = (state, formId, { radius, sizes = {}, colors = {} }) => ({
    radius,
    sizes: {
        top: sizes.top,
        bottom: sizes.bottom,
        right: sizes.right,
        left: sizes.left,
    },
    colors: {
        default: getTicketFormEntityStyle(state, formId, colors.default, ENTITIES_KEY_COLOR),
        hover: getTicketFormEntityStyle(state, formId, colors.hover, ENTITIES_KEY_COLOR),
    },
});

// Specific styles selectors
export const getTicketNameTextStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return getTicketFontStyles(state, formId, get(ticketTypeStylesByCategory, 'ticketName', {}));
};

export const getTicketDateTimeTextStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return getTicketFontStyles(state, formId, get(ticketTypeStylesByCategory, 'ticketDateTime', {}));
};

export const getTicketDescriptionTextStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    const styles = getTicketFontStyles(state, formId, get(ticketTypeStylesByCategory, 'ticketDescription', {}));

    return {
        ...styles,
        fontSize: styles.fontSize || DEFAULT_SMALL_TICKET_FONT_SIZE,
    };
};

export const getTicketRemainingTextStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    const styles = getTicketFontStyles(state, formId, get(ticketTypeStylesByCategory, 'ticketRemaining', {}));

    return {
        ...styles,
        fontSize: styles.fontSize || DEFAULT_SMALL_TICKET_FONT_SIZE,
    };
};

export const getTicketPriceTextStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return getTicketFontStyles(state, formId, get(ticketTypeStylesByCategory, 'ticketPrice', {}));
};

export const getTicketOverlayStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    const { color, opacity, url, baseUrl } = get(ticketTypeStylesByCategory, 'ticketOverlay', {});

    return {
        color: getTicketFormEntityStyle(state, formId, color, ENTITIES_KEY_COLOR),
        opacity,
        imageUrl: url,
        imageBaseUrl: baseUrl,
    };
};

export const getTicketStylesLayout = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return get(ticketTypeStylesByCategory, 'ticketStyle', {});
};

export const getTicketBackgroundStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    const { color, url, baseUrl, opacity } = get(ticketTypeStylesByCategory, 'ticketBackground', {});

    return {
        backgroundColor: getTicketFormEntityStyle(state, formId, color, ENTITIES_KEY_COLOR),
        imageUrl: url,
        imageBaseUrl: baseUrl,
        opacity,
    };
};

export const getTicketBorderStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return geTicketBorders(state, formId, get(ticketTypeStylesByCategory, 'ticketBorder', {}));
};

export const getTicketSoldOutVisibility = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    return get(ticketTypeStylesByCategory, 'soldOutVisibility');
};

export const getTicketQuantityStyles = (state, formId, category) => {
    const ticketTypeStylesByCategory = getTicketStylesByCategory(state, formId, category);
    const { inputType, font, fontSize, fontColors, inputBackgroundColors, colors, sizes, radius } = get(
        ticketTypeStylesByCategory,
        'ticketQuantity',
        {},
    );

    const inputBorder = geTicketBorders(state, formId, { colors, sizes, radius } || {});
    const answerColor = get(fontColors, 'answer');
    const placeholderColor = get(fontColors, 'placeholder');
    const inputBackgroundDefaultColor = get(inputBackgroundColors, 'default');
    const inputBackgroundHoverColor = get(inputBackgroundColors, 'hover');

    return {
        inputType,
        font: getTicketFormEntityStyle(state, formId, font, ENTITIES_KEY_FONT),
        fontSize: fontSize || DEFAULT_SMALL_TICKET_FONT_SIZE,
        fontColors: {
            answer: getTicketFormEntityStyle(state, formId, answerColor, ENTITIES_KEY_COLOR),
            placeholder: getTicketFormEntityStyle(state, formId, placeholderColor, ENTITIES_KEY_COLOR),
        },
        inputBackgroundColors: {
            default: getTicketFormEntityStyle(state, formId, inputBackgroundDefaultColor, ENTITIES_KEY_COLOR),
            hover: getTicketFormEntityStyle(state, formId, inputBackgroundHoverColor, ENTITIES_KEY_COLOR),
        },
        ...inputBorder,
    };
};
